// Ported from files/snapdraw-design-kit/code/src/lib/doc-status.ts (Lovable
// reference implementation), adapted to our real AnyDocument/DocStatus types
// instead of the kit's fixture DocRecord, and using the real current date
// instead of a fixed demo date.
//
// Status lifecycle for quotes and pay applications.
//
// Only three states are ever stored: pending -> accepted -> closed.
// "Overdue" is NOT a status — it is derived from `dueDate` at read time
// (see `isOverdue`), so a late document keeps its real state and simply
// renders an amber "late" modifier next to the chip (StatusChip's `late`
// prop). There is no "mark overdue" action anywhere.
import type { AnyDocument, DocStatus, DueTermId } from "@/lib/types";

export const STATUS_FLOW: Record<DocStatus, DocStatus[]> = {
  draft: ["pending", "void"],
  pending: ["accepted", "closed", "void"],
  accepted: ["closed", "pending", "void"],
  closed: ["pending"],
  void: ["draft"],
  // Both terminal and system-set only (see @/lib/lineage's createRevisionDraft/
  // createInvoiceDraft and @/lib/documents's sendDocument) — no status ever
  // lists these as a manual target, so DocStatusMenu never offers "Mark as
  // Superseded/Invoiced" by hand.
  superseded: [],
  invoiced: [],
};

/**
 * Editability, driven by the fact that SnapDraw never sends anything — the
 * sub downloads or prints the PDF and delivers it themselves.
 *
 * draft    -> free editing, nothing has left the app.
 * pending  -> editable, but saving bumps the revision so the record matches
 *             the copy the client is holding.
 * accepted -> locked. A signed quote changes by change order; a certified
 *             pay application self-corrects in the next period.
 * closed   -> locked, historical.
 * void     -> locked; restore to draft to work on it again.
 */
export type EditMode = "free" | "revision" | "locked";

export const EDIT_MODE: Record<DocStatus, EditMode> = {
  draft: "free",
  pending: "revision",
  accepted: "locked",
  closed: "locked",
  void: "locked",
  superseded: "locked",
  invoiced: "locked",
};

/** Filter views available in the documents list — includes the derived one. */
export type DocView = DocStatus | "overdue" | "all";

/**
 * A document still open/live in the app — the shared exclusion every
 * open-balance/overdue/aging/win-rate/outstanding computation applies.
 * `superseded` (replaced by a newer quote revision) and `invoiced` (billed
 * via a real Invoice, which is now the receivable) join `closed`/`draft`/
 * `void` here — see @/lib/lineage for how those two are set.
 */
export function isOpenStatus(status: DocStatus): boolean {
  return (
    status !== "closed" &&
    status !== "draft" &&
    status !== "void" &&
    status !== "superseded" &&
    status !== "invoiced"
  );
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2026-08-30" -> "Aug 30" */
export function formatDueDate(iso: string): string {
  const [, m, d] = iso.split("-");
  return `${MONTHS[Number(m) - 1]} ${Number(d)}`;
}

export function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

/** "2026-08-30" -> "Aug 30, 2026" — for CSV exports, which need the year (unlike `formatDueDate`, kept year-less for the builder UI). */
export function formatFullDate(iso: string): string {
  const [y, m, d] = iso.split("-");
  return `${MONTHS[Number(m) - 1]} ${Number(d)}, ${y}`;
}

export function addDays(iso: string, days: number): string {
  const date = new Date(`${iso}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

/**
 * Payment terms drive the due date — the sub picks terms, SnapDraw does the
 * date math. Always reads as "payment due" (see @/components/snapdraw/
 * due-terms) — a quote's pricing validity is a separate, independent
 * concept (@/lib/quoteValidity), not this.
 */
export const DUE_TERMS: { id: DueTermId; days: number }[] = [
  { id: "receipt", days: 0 },
  { id: "net15", days: 15 },
  { id: "net30", days: 30 },
  { id: "net45", days: 45 },
];

export function dueDateFromTerms(term: DueTermId, from: string = todayISO()): string {
  const match = DUE_TERMS.find((option) => option.id === term);
  return addDays(from, match?.days ?? 0);
}

/**
 * Terms are always measured from the document's own creation/sent date, not
 * "now" — so changing Net 15 to Net 30 a week later extends the due date
 * from when it was sent, rather than restarting the clock. `date` is the
 * builder's display-formatted creation date (e.g. "Aug 22, 2026"); falls
 * back to today if it can't be parsed.
 */
export function isoFromDisplayDate(date: string): string {
  const parsed = new Date(date);
  return Number.isNaN(parsed.getTime()) ? todayISO() : parsed.toISOString().slice(0, 10);
}

/** Derived, never stored: past due and not yet closed — drafts and void documents never count as overdue. */
export function isOverdue(doc: Pick<AnyDocument, "status" | "dueDate">): boolean {
  return isOpenStatus(doc.status) && !!doc.dueDate && doc.dueDate < todayISO();
}

/** Whole days past the due date; 0 when not late. */
export function daysLate(doc: Pick<AnyDocument, "status" | "dueDate">): number {
  if (!isOverdue(doc)) return 0;
  const ms = Date.parse(`${todayISO()}T00:00:00Z`) - Date.parse(`${doc.dueDate}T00:00:00Z`);
  return Math.max(1, Math.round(ms / 86_400_000));
}

/** How many days out the Dashboard's "Next Up" panel looks for approaching due dates. */
export const NEXT_UP_WINDOW_DAYS = 14;

/** Whole days from today until the due date; 0 on the due date itself, negative once overdue. */
export function daysUntilDue(doc: Pick<AnyDocument, "dueDate">): number | null {
  if (!doc.dueDate) return null;
  const ms = Date.parse(`${doc.dueDate}T00:00:00Z`) - Date.parse(`${todayISO()}T00:00:00Z`);
  return Math.round(ms / 86_400_000);
}

/**
 * The single date Next Up watches for a document: a quote's own `validUntil`
 * (when its price stops holding — quotes have no `dueDate` at all, DOCS-14),
 * or a payable document's real `dueDate`. Never both for the same document.
 */
export function nextUpDate(doc: AnyDocument): string | null {
  return doc.docType === "standard" ? (doc.validUntil ?? null) : doc.dueDate;
}

/** Whole days from today until `iso`; negative once past. */
function daysUntil(iso: string): number {
  const ms = Date.parse(`${iso}T00:00:00Z`) - Date.parse(`${todayISO()}T00:00:00Z`);
  return Math.round(ms / 86_400_000);
}

/**
 * Feeds the Dashboard's "Next Up" panel (backlog J7) — per the design spec,
 * this is deliberately NOT limited to "not yet due": an already-overdue
 * payment or already-expired quote is exactly the kind of time-sensitive
 * item the panel exists to surface (it just sorts above the not-yet-due
 * ones — see `nextUpPriority`). Covers spec scenarios 1/2/5 (approval due
 * soon, quote expiring, payment overdue); scenarios 3/4 (retainage release,
 * billing-period open) need project-schedule data this app doesn't store yet.
 */
export function isNextUpCandidate(doc: AnyDocument): boolean {
  if (!isOpenStatus(doc.status)) return false;
  const date = nextUpDate(doc);
  return date !== null && daysUntil(date) <= NEXT_UP_WINDOW_DAYS;
}

/**
 * Sort key for "Next Up" candidates: most-overdue/most-expired first, then
 * upcoming ones soonest-first — matching the spec's priority order (overdue
 * > due within 3 days > due within 14 days).
 */
export function nextUpPriority(doc: AnyDocument): number {
  const date = nextUpDate(doc);
  if (date === null) return Infinity;
  const days = daysUntil(date);
  return days < 0 ? -1000 + days : days;
}

/**
 * Whole days already past the Next Up watch date — 0 if not reached yet, or
 * if the document has no watch date at all. Quotes are never "late" (that's
 * a payment-term concept, DOCS-14) so this reads as "expired Nd ago" for
 * them in the UI, never "Nd late".
 */
export function nextUpDaysPast(doc: AnyDocument): number {
  const date = nextUpDate(doc);
  if (date === null) return 0;
  const days = daysUntil(date);
  return days < 0 ? -days : 0;
}

/**
 * Date column: a closed document shows its real paid date if one was
 * captured, falling back to the stored creation date for older documents
 * saved before this field existed; everything else shows the due date it's
 * being measured against.
 */
export function docDateLabel(doc: Pick<AnyDocument, "status" | "dueDate" | "date" | "paidDate">): string {
  if (doc.status === "draft" || doc.status === "void") return doc.date;
  if (doc.status === "closed") return doc.paidDate ? `Paid ${formatDueDate(doc.paidDate)}` : doc.date;
  if (!doc.dueDate) return doc.date;
  return `Due ${formatDueDate(doc.dueDate)}`;
}

/** ISO sort key matching whatever docDateLabel() actually shows for this row (paid date, due date, or the raw creation date, depending on status) — so sorting the Date column agrees with what's printed in it, not just `doc.date`. */
export function docDateSortValue(doc: Pick<AnyDocument, "status" | "dueDate" | "date" | "paidDate">): string {
  if (doc.status === "draft" || doc.status === "void") return isoFromDisplayDate(doc.date);
  if (doc.status === "closed") return doc.paidDate ?? isoFromDisplayDate(doc.date);
  if (!doc.dueDate) return isoFromDisplayDate(doc.date);
  return doc.dueDate;
}

/**
 * Powers the Dashboard's "This Quarter" filter — per Lovable's guidance
 * this is a *global* dashboard time filter, not just the KPI tiles: it also
 * bounds Recent Documents, Cash Timeline (via `quarterMonths` below) and
 * Next Up, so the numbers on screen never disagree with each other.
 */
export function isoInQuarter(iso: string): boolean {
  const d = new Date(`${iso}T00:00:00Z`);
  const now = new Date(`${todayISO()}T00:00:00Z`);
  return d.getUTCFullYear() === now.getUTCFullYear() && Math.floor(d.getUTCMonth() / 3) === Math.floor(now.getUTCMonth() / 3);
}

/**
 * A document's relevant date for quarter membership is its real `paidDate`
 * once closed (the actual cash event, same reasoning as
 * `computeCashTimeline`), falling back to creation date otherwise —
 * matching `docDateLabel`'s convention.
 */
export function isThisQuarter(doc: Pick<AnyDocument, "status" | "date" | "paidDate">): boolean {
  const iso = doc.status === "closed" ? (doc.paidDate ?? isoFromDisplayDate(doc.date)) : isoFromDisplayDate(doc.date);
  return isoInQuarter(iso);
}

/** The 3 calendar months (oldest first) of the quarter `today` falls in — for Cash Timeline scoped to "This Quarter". */
export function quarterMonths(): { key: string; label: string }[] {
  const now = new Date(`${todayISO()}T00:00:00Z`);
  const startMonth = Math.floor(now.getUTCMonth() / 3) * 3;
  return Array.from({ length: 3 }, (_, i) => {
    const d = new Date(Date.UTC(now.getUTCFullYear(), startMonth + i, 1));
    return { key: `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`, label: MONTHS[d.getUTCMonth()] };
  });
}

/** i18n key for the menu item that moves a document to `status`. */
export function moveActionKey(status: DocStatus): `docs.move.to.${DocStatus}` {
  return `docs.move.to.${status}`;
}

/** Short type badge shown on document rows/cards ("QTE"/"G702"/"INV"). */
export function docTypeLabel(docType: AnyDocument["docType"]): "QTE" | "G702" | "INV" {
  if (docType === "g702") return "G702";
  if (docType === "invoice") return "INV";
  return "QTE";
}

/**
 * Cash timeline input (backlog J3 — see computeCashTimeline below).
 *
 * `paidDate` is captured explicitly when a document is closed — the
 * status-change timestamp is NOT a usable proxy, because subs batch their
 * close-outs (a whole week of jobs marked paid on Friday) and every bar in
 * a future cash-timeline chart would land in the wrong bucket. Default the
 * picker to today, but always let them back-date.
 */
export const PAID_QUICK_PICKS: { id: "today" | "yesterday" | "week"; days: number }[] = [
  { id: "today", days: 0 },
  { id: "yesterday", days: -1 },
  { id: "week", days: -7 },
];

export type PaidQuickPickId = (typeof PAID_QUICK_PICKS)[number]["id"];

export function paidDateFromPick(pick: PaidQuickPickId, from: string = todayISO()): string {
  const match = PAID_QUICK_PICKS.find((option) => option.id === pick);
  return addDays(from, match?.days ?? 0);
}

export interface CashTimelineBar {
  /** "2026-08" — stable key for the list `key` prop. */
  key: string;
  /** "Aug" — CSS (`sd-label`) uppercases it for display. */
  label: string;
  amount: number;
  /** 0-100, relative to the tallest bar in this series — matches the design kit's bar-chart convention. */
  height: number;
}

function trailingSixMonths(): { key: string; label: string }[] {
  const now = new Date();
  return Array.from({ length: 6 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
    return { key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`, label: MONTHS[d.getMonth()] };
  });
}

/**
 * Money actually collected, bucketed by month — summed from each closed
 * document's real `paidDate`, never a status-change timestamp (see
 * PAID_QUICK_PICKS above for why). Defaults to the trailing 6 calendar
 * months (oldest first, current month last); pass `quarterMonths()` to
 * scope it to the Dashboard's "This Quarter" filter instead.
 */
export function computeCashTimeline(
  docs: Pick<AnyDocument, "status" | "paidDate" | "grandTotal">[],
  months: { key: string; label: string }[] = trailingSixMonths(),
): CashTimelineBar[] {
  const totals = new Map(months.map((m) => [m.key, 0]));
  for (const doc of docs) {
    if (doc.status !== "closed" || !doc.paidDate) continue;
    const key = doc.paidDate.slice(0, 7);
    if (!totals.has(key)) continue;
    const amount = parseFloat((doc.grandTotal ?? "").replace(/[^0-9.-]/g, "")) || 0;
    totals.set(key, (totals.get(key) ?? 0) + amount);
  }

  const max = Math.max(1, ...totals.values());
  return months.map((m) => {
    const amount = totals.get(m.key) ?? 0;
    return { ...m, amount, height: Math.round((amount / max) * 100) };
  });
}

function parseAmount(grandTotal: string | undefined): number {
  return parseFloat((grandTotal ?? "").replace(/[^0-9.-]/g, "")) || 0;
}

/**
 * A "receivable" in this app is a sent G702/G703 pay application or a real
 * Invoice — a quote is a proposal, not money requested, so it's never
 * counted as invoiced or aged (an accepted-but-unbilled quote isn't a
 * receivable yet; see @/lib/lineage's canInvoice for converting one). This
 * scoping is specific to the Reports/Receivables page (backlog H4 v2, widened
 * for backlog N/Rule 18) — Dashboard's and the Clients directory's own
 * "outstanding" figures deliberately keep counting all open documents,
 * which is correct for their own contexts.
 */
export function isReceivable(doc: Pick<AnyDocument, "docType">): boolean {
  return doc.docType === "g702" || doc.docType === "invoice";
}

/**
 * Money invoiced (billed), bucketed by month — summed from every open
 * receivable's creation timestamp (`savedAt`), G702/G703 only (see
 * `isReceivable`). This is the "billed this period" half of the Reports
 * invoiced-vs-collected pair; `computeCashTimeline` above is the "actually
 * paid" half, and stays type-agnostic — cash collected isn't scoped to how
 * it was invoiced. Same trailing-6-month default as `computeCashTimeline`.
 */
export function computeInvoicedTimeline(
  docs: Pick<AnyDocument, "docType" | "status" | "savedAt" | "grandTotal">[],
  months: { key: string; label: string }[] = trailingSixMonths(),
): CashTimelineBar[] {
  const totals = new Map(months.map((m) => [m.key, 0]));
  for (const doc of docs) {
    if (!isReceivable(doc) || doc.status === "draft" || doc.status === "void" || !doc.savedAt) continue;
    const key = doc.savedAt.slice(0, 7);
    if (!totals.has(key)) continue;
    totals.set(key, (totals.get(key) ?? 0) + parseAmount(doc.grandTotal));
  }

  const max = Math.max(1, ...totals.values());
  return months.map((m) => {
    const amount = totals.get(m.key) ?? 0;
    return { ...m, amount, height: Math.round((amount / max) * 100) };
  });
}

/**
 * Quotes only, once sent — a sent pay application isn't "won or lost" the
 * way a bid is, so it never enters this ratio. `superseded` is excluded
 * from the denominator entirely: a superseded original and its accepted
 * revision are the same underlying bid, and only the live tip of the chain
 * should count, or one job double-counts as two bids. `invoiced` counts as
 * "won" alongside `accepted`/`closed` — it was accepted, then billed.
 * Returns null when there are no quotes at all.
 */
export function computeWinRate(docs: Pick<AnyDocument, "docType" | "status">[]): number | null {
  const quotes = docs.filter(
    (d) => d.docType === "standard" && d.status !== "draft" && d.status !== "void" && d.status !== "superseded",
  );
  if (quotes.length === 0) return null;
  const won = quotes.filter((d) => d.status === "accepted" || d.status === "closed" || d.status === "invoiced").length;
  return Math.round((won / quotes.length) * 100);
}

export type AgingBucketKey = "current" | "days1to30" | "days31to60" | "days60plus";

export interface AgingBucket {
  key: AgingBucketKey;
  amount: number;
  count: number;
  /** 0-100, this bucket's share of the total open balance — for a progress-bar fill width. */
  share: number;
}

/** null when the document isn't an open receivable (a quote, or already closed). */
export function agingBucketOf(doc: Pick<AnyDocument, "status" | "dueDate" | "docType">): AgingBucketKey | null {
  if (!isReceivable(doc) || !isOpenStatus(doc.status)) return null;
  const late = daysLate(doc);
  return late === 0 ? "current" : late <= 30 ? "days1to30" : late <= 60 ? "days31to60" : "days60plus";
}

/**
 * Open receivable value, bucketed by how many days past due each pay
 * application is — "Current" covers not-yet-due open receivables alongside
 * genuinely on-time ones, matching the design kit's 4-bucket A/R aging
 * convention. Quotes never appear here (see `isReceivable`/`agingBucketOf`).
 */
export function computeAgingBuckets(
  docs: Pick<AnyDocument, "status" | "dueDate" | "docType" | "grandTotal">[],
): AgingBucket[] {
  const totals: Record<AgingBucketKey, { amount: number; count: number }> = {
    current: { amount: 0, count: 0 },
    days1to30: { amount: 0, count: 0 },
    days31to60: { amount: 0, count: 0 },
    days60plus: { amount: 0, count: 0 },
  };
  for (const doc of docs) {
    const key = agingBucketOf(doc);
    if (!key) continue;
    totals[key].amount += parseAmount(doc.grandTotal);
    totals[key].count += 1;
  }

  const total = Math.max(1, Object.values(totals).reduce((sum, b) => sum + b.amount, 0));
  return (Object.keys(totals) as AgingBucketKey[]).map((key) => ({
    key,
    amount: totals[key].amount,
    count: totals[key].count,
    share: Math.round((totals[key].amount / total) * 100),
  }));
}

/** Total value of open receivables right now — the Reports hero's "Outstanding right now." */
export function openBalance(docs: Pick<AnyDocument, "status" | "docType" | "grandTotal">[]): number {
  return docs
    .filter((d) => isReceivable(d) && isOpenStatus(d.status))
    .reduce((sum, d) => sum + parseAmount(d.grandTotal), 0);
}

/** The past-due slice of `openBalance` — every open receivable in any bucket other than "current". */
export function overdueBalance(
  docs: Pick<AnyDocument, "status" | "dueDate" | "docType" | "grandTotal">[],
): number {
  return docs
    .filter((d) => {
      const bucket = agingBucketOf(d);
      return bucket !== null && bucket !== "current";
    })
    .reduce((sum, d) => sum + parseAmount(d.grandTotal), 0);
}
